import "./assessment-template.scss";

import { observer } from "mobx-react";
import React, { useEffect, useRef, useState } from "react";
import { generatePath, Link, useHistory } from "react-router-dom";

import { IconButton, Typography } from "@/components/Elements";
import Dropdown, {
  DropdownOption,
} from "@/components/Elements/Dropdown/Dropdown";
import TextInput from "@/components/form/TextInput";
import Loading from "@/components/Loading";
import { useMainStore } from "@/contexts/Store";
import { ROUTES } from "@/features/risk-assessment";
import { ConfirmationPopup } from "@/modules/shared/confirmation-popup/ConfirmationPopup";
import { getCountStringOfItem } from "@/utils/utils";

import { AssessmentUpsert } from "../../../types/assessment";

type Props = {
  assessmentTemplate: Partial<AssessmentUpsert> | null;
  onChange: (assessment: Partial<AssessmentUpsert>) => void;
};

function AssessmentTemplate({ assessmentTemplate, onChange }: Props) {
  const mainStore = useMainStore();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(true);
  const moduleWorkspace = mainStore.moduleWorkspaces.list.find(
    (item) => item.themis_module.identifier === "risk_assessment",
  );
  const { activeWorkspace, workspaceID } = mainStore.context;
  const nameRef = useRef<HTMLInputElement | null>(null);
  const riskMethodologies = mainStore.riskMethodologies.list;
  // @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
  const riskMethodologyOptions: DropdownOption[] = riskMethodologies.map(
    (methodology) => ({
      id: String(methodology.id),
      name: methodology.name,
    }),
  );
  const { questionnaireTemplates } = mainStore.riskAssessmentQuestionnaires;
  const questionnaireTemplateOptions: DropdownOption[] = questionnaireTemplates
    .filter((questionnaire) => {
      return assessmentTemplate?.risk_methodology_id
        ? questionnaire.risk_methodology_id ===
            assessmentTemplate.risk_methodology_id
        : true;
    })
    .filter((questionnaire) => {
      return !assessmentTemplate?.questionnaire_ids?.includes(questionnaire.id);
    })
    .map((questionnaire) => ({
      id: String(questionnaire.id),
      name: questionnaire.name,
    }));

  useEffect(() => {
    if (!moduleWorkspace || !activeWorkspace) {
      return;
    }

    Promise.all([
      mainStore.riskMethodologies.index(activeWorkspace.id),
      mainStore.riskAssessmentQuestionnaires.indexQuestionnaires(
        moduleWorkspace.id,
      ),
    ])
      .then(() => {
        setIsLoading(false);
      })
      .catch((error: unknown) => {
        mainStore.toast.setErrorFromResponse(error);
        history.push(
          generatePath(ROUTES.TEMPLATE_PATH, {
            workspace_id: Number(workspaceID),
          }),
        );
      });
  }, [moduleWorkspace, activeWorkspace]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (!questionnaireTemplates.length) {
      mainStore.toast.setText(
        "A Questionnaire Template is required to create an Assessment Template. Redirecting...",
      );
      history.push(
        generatePath(ROUTES.QUESTIONNAIRE_TEMPLATE_CREATION_PATH, {
          workspace_id: Number(workspaceID),
        }),
      );
    }
  }, [isLoading, questionnaireTemplates, riskMethodologies]);

  const onNameChange = (name: string): void => {
    onChange({
      ...assessmentTemplate,
      name: name.trim(),
    });
  };

  const onRiskMethodologyChange = (id: number): void => {
    // @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
    if (id === assessmentTemplate.risk_methodology_id) {
      return;
    }

    onChange({
      ...assessmentTemplate,
      risk_methodology_id: id,
      questionnaire_ids: [],
    });
  };

  const onQuestionnaireTemplateAdded = (id: number): void => {
    onChange({
      ...assessmentTemplate,
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      risk_methodology_id: questionnaireTemplates.find(
        (questionnaire) => questionnaire.id === id,
      ).risk_methodology_id,
      questionnaire_ids: [
        // @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
        ...(assessmentTemplate.questionnaire_ids || []),
        id,
      ],
    });
  };

  const onQuestionnaireTemplateDeleted = (id: number): void => {
    onChange({
      ...assessmentTemplate,
      // @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
      questionnaire_ids: assessmentTemplate.questionnaire_ids.filter(
        (_id) => _id != id,
      ),
    });
  };

  if (isLoading || !assessmentTemplate) {
    return <Loading loadingLayout="small-table" />;
  }

  return (
    <div className="assessment-template">
      <div className="assessment-template__content">
        <Typography
          label="Assessment Details"
          color="generalMidnightDark"
          size="lg"
          weight="semiBold"
          className="assessment-template__heading"
        />
        <div className="assessment-template__name-and-methodology">
          <div>
            <label htmlFor="assessment-name">Assessment Name</label>
            <TextInput
              className="assessment-template__name-input"
              inputRef={nameRef.current}
              defaultValue={assessmentTemplate.name || ""}
              placeholder="- Type here -"
              name="assessment-name"
              id="assessment-name"
              onChange={(e) => onNameChange(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="risk-methodology">Risk Methodology</label>
            <Dropdown
              options={riskMethodologyOptions}
              onChange={(selected) =>
                onRiskMethodologyChange(Number(selected.id))
              }
              selected={String(assessmentTemplate.risk_methodology_id)}
              placeholder="- Select -"
              id="risk-methodology-dropdown"
            />
          </div>
        </div>
        {assessmentTemplate.risk_methodology_id &&
        !assessmentTemplate.questionnaire_ids?.length &&
        questionnaireTemplateOptions.length === 0 ? (
          <Typography
            label="There are no Questionnaire Templates with this selected Risk Methodology"
            weight="semiBold"
            color="generalDarkGray"
          />
        ) : (
          <div className="assessment-template__questionnaires-heading">
            <Typography
              label="Questionnaire Templates"
              color="generalMidnightDark"
              size="lg"
              weight="semiBold"
            />
            <Typography
              label={getCountStringOfItem(
                assessmentTemplate.questionnaire_ids?.length,
                "Template",
              )}
              weight="semiBold"
              color="generalDarkGray"
            />
            <Dropdown
              id="questionnaire-template-dropdown"
              className="assessment-template__questionnaires-dropdown"
              options={questionnaireTemplateOptions}
              onChange={(selected) =>
                onQuestionnaireTemplateAdded(Number(selected.id))
              }
              placeholder={
                questionnaireTemplateOptions.length == 0
                  ? "No more options"
                  : "- Select -"
              }
            />
          </div>
        )}
        {/* @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'. */}
        {assessmentTemplate.questionnaire_ids?.length > 0 && (
          <div className="assessment-template__questionnaires">
            {/* @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'. */}
            {assessmentTemplate.questionnaire_ids.map((id) => (
              <div className="assessment-template__questionnaire" key={id}>
                <Link
                  className="assessment-template__questionnaire-link"
                  to={generatePath(ROUTES.QUESTIONNAIRE_TEMPLATE_EDIT_PATH, {
                    templateId: id,
                    workspace_id: Number(workspaceID),
                  })}
                >
                  <Typography
                    label={
                      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
                      questionnaireTemplates.find(
                        (template) => template.id == id,
                      ).name
                    }
                    color="brandingHighlightTurquoise"
                  />
                </Link>
                <ConfirmationPopup
                  text="Are you sure you want to delete this questionnaire template?"
                  onConfirm={() => onQuestionnaireTemplateDeleted(id)}
                  trigger={
                    <IconButton
                      className="assessment-template__questionnaire-delete-button"
                      icon="trash"
                    />
                  }
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default observer(AssessmentTemplate);
